import { useEffect } from 'react';
import { create } from 'zustand';

import { snapshotVersionOf } from '@sb/database/snapshotParams';
import { makeLogNamespace } from '@sb/log/log';
import type { EnvironmentVariable } from '@sb/remote-control/types';
import { createGlobalVariable } from '@sbrc/services/feathers-client/global-variables/createGlobalVariable';
import { deleteGlobalVariable } from '@sbrc/services/feathers-client/global-variables/deleteGlobalVariable';
import { onGetAllGlobalVariables } from '@sbrc/services/feathers-client/global-variables/onGetAllGlobalVariables';
import { updateGlobalVariable } from '@sbrc/services/feathers-client/global-variables/updateGlobalVariable';

const log = makeLogNamespace('GlobalVariablesStore');

interface GlobalVariablesStore {
  globalVariables: EnvironmentVariable[];
  itemIdToGlobalKey: { [key: string]: string };
  isLoading: boolean;
  isSubscribed: boolean;
  addGlobalVariable: (variable: EnvironmentVariable) => Promise<void>;
  modifyGlobalVariable: (variable: EnvironmentVariable) => Promise<void>;
  removeGlobalVariable: (variableId: string) => Promise<void>;
  initializeGlobalVariables: () => void;
}

export const useGlobalVariablesStore = create<GlobalVariablesStore>(
  (set, get) => {
    return {
      globalVariables: [],
      itemIdToGlobalKey: {},
      isLoading: true,
      isSubscribed: false,

      addGlobalVariable: async (variable: EnvironmentVariable) => {
        // Check for uniqueness
        const existingVariable = get().globalVariables.find(
          (v) => v.id === variable.id,
        );

        if (existingVariable) {
          return;
        }

        try {
          const saved = await createGlobalVariable(variable);
          const globalId = saved.globalVariableId;

          // A watch snapshot emitted for this create may already have
          // delivered the variable before this local append runs — only
          // append when it hasn't, so the variable can't be duplicated.
          set((state) => ({
            globalVariables: state.globalVariables.some(
              (v) => v.id === variable.id,
            )
              ? state.globalVariables
              : [...state.globalVariables, saved.globalVariableItem],
            itemIdToGlobalKey: {
              ...state.itemIdToGlobalKey,
              [variable.id]: globalId,
            },
          }));
        } catch (e) {
          log.error('addVariable.error', 'Failed to add global variable', {
            e,
          });
        }
      },

      modifyGlobalVariable: async (updatedVariable: EnvironmentVariable) => {
        try {
          const globalVariableItemId =
            get().itemIdToGlobalKey[updatedVariable.id];

          const saved = await updateGlobalVariable(
            globalVariableItemId,
            updatedVariable,
          );

          // Update state with the modified variable
          set((state) => ({
            globalVariables: state.globalVariables.map((variable) =>
              variable.id === updatedVariable.id &&
              !(
                Date.parse(String(snapshotVersionOf(variable))) >
                Date.parse(String(snapshotVersionOf(saved)))
              )
                ? saved
                : variable,
            ),
          }));
        } catch (e) {
          log.error(
            'updateVariable.error',
            'Failed to update global variable',
            { e },
          );

          throw e;
        }
      },

      removeGlobalVariable: async (itemId: string) => {
        try {
          const globalVariableItemId = get().itemIdToGlobalKey[itemId];
          await deleteGlobalVariable(globalVariableItemId);

          // Remove the variable from state
          set((state) => ({
            globalVariables: state.globalVariables.filter(
              (v) => v.id !== itemId,
            ),
            itemIdToGlobalKey: Object.fromEntries(
              Object.entries(state.itemIdToGlobalKey).filter(
                ([key]) => key !== itemId,
              ),
            ),
          }));
        } catch (e) {
          log.error(
            'deleteVariable.error',
            'Failed to delete global variable',
            { e },
          );
        }
      },

      // Watches global variables for the lifetime of the store so
      // server-side updates (e.g. persisted variable values written while a
      // routine runs) reach consumers. All callers share one subscription.
      initializeGlobalVariables: () => {
        if (get().isSubscribed) return;

        set({ isLoading: true, isSubscribed: true });

        try {
          onGetAllGlobalVariables((globalVariablesItemsWithID) => {
            const itemIdToGlobalKey: { [key: string]: string } = {};
            const globalVariablesItems: EnvironmentVariable[] = [];

            for (const itemWithID of globalVariablesItemsWithID) {
              itemIdToGlobalKey[itemWithID.globalVariableItem.id] =
                itemWithID.globalVariableId;

              globalVariablesItems.push(itemWithID.globalVariableItem);
            }

            set({
              globalVariables: globalVariablesItems,
              itemIdToGlobalKey,
              isLoading: false,
            });
          });
        } catch (error) {
          log.error(
            'initializeGlobalVariables.error',
            'Failed to initialize global variables',
            { error },
          );

          set({ isLoading: false, isSubscribed: false });
        }
      },
    };
  },
);

export function useInitializeGlobalVariables() {
  const initializeGlobalVariables = useGlobalVariablesStore(
    (state) => state.initializeGlobalVariables,
  );

  useEffect(() => {
    initializeGlobalVariables();
  }, [initializeGlobalVariables]);
}
